/* global Promise */
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
} from 'antd';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import {Button as DBButton} from '@dabafinance/react-components';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum, supportedLanguages} from 'utils/constants';
import {
  EXPORT_HOLDINGS_AUDIENCE,
  HOLDINGS_AUDIENCE_ASSET_CLASSES,
  HOLDINGS_AUDIENCE_INSTRUMENTS,
  PREVIEW_HOLDINGS_AUDIENCE,
} from 'graphql/queries/notifications';
import {
  HOLDINGS_EXCHANGES,
  HOLDINGS_FIELDS,
  holdingsSelectionCount,
  isHoldingsAmount,
  itemValue,
  showHoldingsOperator,
} from '../utils/holdingsAudience';
import {WALLET_CURRENCIES} from '../utils/walletAudience';
import styles from './walletActivityFilter.module.scss';
import own from './holdingsFilter.module.scss';

const PAGE_SIZE = 20;

/** Set by a preview, read on submit to warn before scheduling to nobody. */
export const HOLDINGS_PREVIEW_COUNT = 'holdingsPreviewCount';

const useHoldingsValues = () => {
  const form = Form.useFormInstance();
  const values = {audience: Form.useWatch('audience', form)};
  HOLDINGS_FIELDS.forEach(field => {
    values[field] = Form.useWatch(field, form);
  });
  return values;
};

const asOfLabel = asOf => (asOf ? moment(asOf).format('HH:mm') : '—');

const Freshness = ({asOf, stale}) => (
  <span className={styles.hint}>
    Holdings as of {asOfLabel(asOf)}
    {stale && (
      <Tag color="orange" style={{marginLeft: 8}}>
        Over 15 minutes old
      </Tag>
    )}
  </span>
);

/** BRVM / NGX as toggle chips. */
const ExchangeChips = ({value = [], onChange}) => (
  <div>
    {HOLDINGS_EXCHANGES.map(exchange => (
      <Tag.CheckableTag
        key={exchange}
        checked={value.includes(exchange)}
        onChange={checked =>
          onChange(
            checked
              ? [...value, exchange]
              : value.filter(item => item !== exchange),
          )
        }>
        {exchange}
      </Tag.CheckableTag>
    ))}
  </div>
);

/**
 * Searchable, paged picker over held instruments, or over collections when
 * scoped to Collections. Values are labelInValue entries keyed `KIND:id`.
 */
const InstrumentPicker = ({
  value,
  onChange,
  assetClassOptions,
  includeUnsettled,
}) => {
  const client = useApolloClient();
  const [scope, setScope] = useState();
  const [exchange, setExchange] = useState();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);

  const load = page => {
    const id = ++request.current;
    setLoading(true);
    return client
      .query({
        query: HOLDINGS_AUDIENCE_INSTRUMENTS,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            ...(scope && {assetClasses: [scope]}),
            ...(search && {search}),
            ...(exchange && {exchange}),
            page,
            limit: PAGE_SIZE,
            includeUnsettled: Boolean(includeUnsettled),
          },
        },
      })
      .then(({data}) => {
        if (id !== request.current) return;
        const result = data?.adminHoldingsAudienceInstruments;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
          return;
        }
        setRows(prev =>
          page === 1 ? result?.data ?? [] : [...prev, ...(result?.data ?? [])],
        );
        setPagination(result?.pagination ?? null);
      })
      .catch(error => toast.error(error.message))
      .finally(() => id === request.current && setLoading(false));
  };

  useEffect(() => {
    load(1);
  }, [scope, exchange, search, includeUnsettled]);

  const debouncedSearch = useMemo(
    () => _.debounce(term => setSearch(term.trim()), 300),
    [],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <div className={styles.grid}>
      <Form.Item
        label="Look in"
        tooltip="Narrows the list below to one asset class. It only helps you find instruments; it doesn't change who receives the notification. Choose Collections to list collections."
        className={styles.field}>
        <Select
          allowClear
          placeholder="All instruments"
          value={scope}
          onChange={setScope}
          options={assetClassOptions.map(({key, label}) => ({
            value: key,
            label,
          }))}
        />
      </Form.Item>
      <Form.Item
        label="Exchange"
        tooltip="Narrows the list below to one exchange. It only helps you find instruments; to target holders on an exchange, use Exchange under Narrow it down."
        className={styles.field}>
        <Select
          allowClear
          placeholder="Any exchange"
          value={exchange}
          onChange={setExchange}
          options={HOLDINGS_EXCHANGES.map(code => ({value: code, label: code}))}
        />
      </Form.Item>
      <Form.Item
        label={
          scope === 'COLLECTIONS' ? 'Collections' : 'Instruments or collections'
        }
        tooltip="Targets users who hold these specific instruments or collections. An instrument narrows its own class: Stocks + SONATEL means SONATEL holders only. Only instruments someone holds are listed, most held first."
        className={styles.dates}>
        <Select
          mode="multiple"
          labelInValue
          showSearch
          allowClear
          filterOption={false}
          optionLabelProp="label"
          placeholder="Search by ticker or name"
          value={value}
          onChange={onChange}
          onSearch={debouncedSearch}
          onBlur={() => debouncedSearch('')}
          notFoundContent={
            loading ? <Spin size="small" /> : 'No held instruments match'
          }
          dropdownRender={menu => (
            <>
              {menu}
              {pagination?.hasNextPage && (
                <Button
                  type="link"
                  block
                  disabled={loading}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => load((pagination.page || 1) + 1)}>
                  {loading
                    ? 'Loading…'
                    : `Load more (${rows.length} of ${pagination.total})`}
                </Button>
              )}
            </>
          )}>
          {rows.map(row => (
            <Select.Option
              key={itemValue(row.kind, row.id)}
              value={itemValue(row.kind, row.id)}
              label={row.ticker || row.name}>
              <strong>{row.ticker || row.name}</strong>
              {row.ticker && ` · ${row.name}`}
              <span className={styles.hint} style={{display: 'inline'}}>
                {' '}
                · {row.exchange || '—'} · {row.holderCount} holder
                {row.holderCount === 1 ? '' : 's'}
              </span>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );
};

const languageLabel = code =>
  supportedLanguages.find(language => language.value === code)?.label ||
  code ||
  '—';

const SAMPLE_COLUMNS = [
  {
    title: 'Name',
    key: 'name',
    render: (_value, user) =>
      [user.firstName, user.lastNameInitial].filter(Boolean).join(' ') || '—',
  },
  {title: 'Email', dataIndex: 'email', render: v => v || '—'},
  {title: 'Country', dataIndex: 'country', render: v => v || '—'},
  {title: 'Language', dataIndex: 'language', render: languageLabel},
];

const PreviewResult = ({preview}) => (
  <div className={styles.group}>
    <h3>
      Estimated recipients: {preview.estimatedRecipients.toLocaleString()}
    </h3>
    <span className={styles.hint}>
      Before channel, preference and language checks.{' '}
      {preview.holderCount.toLocaleString()} user
      {preview.holderCount === 1 ? '' : 's'} match the holdings alone.
      {preview.instrumentTargeted && (
        <Tag color="#8d6cfa" style={{marginLeft: 8}}>
          Instrument-targeted
        </Tag>
      )}
    </span>
    <Freshness asOf={preview.asOf} stale={preview.stale} />
    {preview.estimatedRecipients === 0 ? (
      <Alert
        type="warning"
        showIcon
        style={{margin: '8px 0'}}
        message={preview.message || 'No users match this audience.'}
      />
    ) : (
      (!preview.exact || preview.message) && (
        <Alert
          type="info"
          showIcon
          style={{margin: '8px 0'}}
          message={preview.message || 'This count is approximate.'}
          description={
            preview.notApplied?.length
              ? `Not counted: ${preview.notApplied.join(', ')}`
              : undefined
          }
        />
      )
    )}
    {preview.sample?.length > 0 && (
      <Table
        size="small"
        rowKey="id"
        style={{marginBottom: 12}}
        columns={SAMPLE_COLUMNS}
        dataSource={preview.sample}
        pagination={false}
      />
    )}
  </div>
);

/**
 * Portfolio holdings audience filters, with preview and export. Selections
 * combine as described in the hand-off: an instrument narrows its own class,
 * other classes stay whole, and an exchange narrows everything else.
 *
 * `getPreviewInput` returns the HoldingsAudiencePreviewInput for the whole
 * form, so the preview counts every other filter too.
 */
export const HoldingsFilter = ({getPreviewInput}) => {
  const form = Form.useFormInstance();
  const client = useApolloClient();
  const values = useHoldingsValues();
  const selections = holdingsSelectionCount(values);
  const includeUnsettled = Boolean(values.holdingsIncludeUnsettled);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const canExport = Boolean(
    useSelector(state =>
      makeSelectTabOperations(state, NavTabsEnum.NotificationSchedule),
    )?.export,
  );

  const {data: classData, loading: loadingClasses} = useQuery(
    HOLDINGS_AUDIENCE_ASSET_CLASSES,
    {
      variables: {includeUnsettled},
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
      onCompleted: data => {
        const result = data?.adminHoldingsAudienceAssetClasses;
        if (result?.__typename === 'Error') toast.error(result.message);
      },
    },
  );
  const classResult = classData?.adminHoldingsAudienceAssetClasses;
  const assetClasses =
    classResult?.__typename === 'Error' ? [] : classResult?.data ?? [];

  const [exportAudience, {loading: exporting}] = useMutation(
    EXPORT_HOLDINGS_AUDIENCE,
    {errorPolicy: 'all', onError: error => toast.error(error.message)},
  );

  // A preview describes the filters it ran on; any change makes it stale.
  // ponytail: only holdings fields and the audience reset it, not the other
  // audience filters; watch the whole form if that proves misleading.
  const watchedKey = JSON.stringify(values);
  useEffect(() => {
    setPreview(null);
    form.setFieldValue(HOLDINGS_PREVIEW_COUNT, undefined);
  }, [watchedKey, form]);

  const runPreview = () => {
    setPreviewing(true);
    client
      .query({
        query: PREVIEW_HOLDINGS_AUDIENCE,
        variables: {input: getPreviewInput()},
        fetchPolicy: 'no-cache',
      })
      .then(({data}) => {
        const result = data?.previewHoldingsAudience;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
          return;
        }
        setPreview(result.data);
        form.setFieldValue(
          HOLDINGS_PREVIEW_COUNT,
          result.data.estimatedRecipients,
        );
      })
      .catch(error => toast.error(error.message))
      .finally(() => setPreviewing(false));
  };

  const runExport = () =>
    exportAudience({variables: {input: getPreviewInput()}}).then(({data}) => {
      const result = data?.exportHoldingsAudience;
      if (!result) return;
      if (result.__typename === 'Error') {
        toast.error(result.message);
      } else {
        toast.success(
          `Export of ${result.data.recipientCount} users sent to ${result.data.recipient}`,
        );
      }
    });

  return (
    <div className={`${styles.root} ${own.brand}`}>
      <div className={selections ? styles.summaryOn : styles.summary}>
        <span>
          {selections
            ? 'Users are matched on what they hold when the notification is sent, not when it is scheduled.'
            : 'No holdings filter yet. Pick asset classes, instruments, collections or an exchange.'}
        </span>
        {selections > 0 && (
          <Button
            size="small"
            type="link"
            onClick={() =>
              form.setFieldsValue(
                Object.fromEntries(HOLDINGS_FIELDS.map(f => [f, undefined])),
              )
            }>
            Clear holdings filters
          </Button>
        )}
      </div>

      <section className={styles.group}>
        <h3>What they hold</h3>
        {classResult && classResult.__typename !== 'Error' && (
          <Freshness asOf={classResult.asOf} stale={classResult.stale} />
        )}
        <div className={styles.grid}>
          <Form.Item
            name="holdingsAssetClasses"
            label="Asset classes"
            tooltip="Targets users who hold anything in these classes. Counts are users holding each class today; they refresh every few minutes and are a guide, not the final number."
            className={styles.dates}>
            <Select
              mode="multiple"
              allowClear
              loading={loadingClasses}
              optionLabelProp="label"
              placeholder="Any asset class">
              {assetClasses.map(assetClass => (
                <Select.Option
                  key={assetClass.key}
                  value={assetClass.key}
                  label={assetClass.label}>
                  {assetClass.label}{' '}
                  <span className={styles.hint} style={{display: 'inline'}}>
                    · {assetClass.holderCount} holder
                    {assetClass.holderCount === 1 ? '' : 's'}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <Form.Item name="holdingsItems" noStyle>
          <InstrumentPicker
            assetClassOptions={assetClasses}
            includeUnsettled={includeUnsettled}
          />
        </Form.Item>
        <span className={styles.hint}>
          An instrument narrows its own class: Stocks + SONATEL means SONATEL
          holders, not every stock holder. Holder counts are refreshed every few
          minutes.
        </span>
      </section>

      <section className={styles.group}>
        <h3>Narrow it down</h3>
        <div className={styles.grid}>
          {showHoldingsOperator(values) && (
            <Form.Item
              name="holdingsOperator"
              label="Match"
              tooltip="Any selection: users holding at least one of the things you picked. Every selection: users holding all of them."
              initialValue="OR"
              className={styles.field}>
              <Radio.Group buttonStyle="solid">
                <Radio.Button value="OR">Any selection</Radio.Button>
                <Radio.Button value="AND">Every selection</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item
            name="holdingsExchanges"
            label="Exchange"
            tooltip="Only counts holdings on these exchanges. On its own, targets anyone holding something there; with other selections, narrows them (Stocks + NGX means NGX stocks)."
            className={styles.field}>
            <ExchangeChips />
          </Form.Item>
          <div className={styles.amount}>
            <Form.Item
              name="holdingsMinAmount"
              label="Minimum holding value"
              tooltip="Each selection must be met by a holding worth at least this much, priced in the chosen currency. Holdings in other currencies never qualify (no conversion). Leave empty for no minimum; 0 still excludes other currencies and unpriced instruments."
              rules={[
                {
                  validator: (_rule, value) =>
                    isHoldingsAmount(value)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Use a number like 100000 or 0'),
                        ),
                },
              ]}
              className={styles.value}>
              <Input
                aria-label="Minimum holding value"
                inputMode="decimal"
                placeholder="No minimum"
              />
            </Form.Item>
            <Form.Item
              name="holdingsMinCurrency"
              label=" "
              initialValue="XOF"
              style={{flex: '0 0 96px'}}>
              <Select options={WALLET_CURRENCIES} />
            </Form.Item>
          </div>
          <Form.Item
            name="holdingsIncludeUnsettled"
            label="Include unsettled positions"
            tooltip="Off: only settled positions count (allocated IPO shares count, IPO subscriptions don't). On: also counts trades that haven't settled yet. Holder counts above follow this switch."
            valuePropName="checked"
            className={styles.field}>
            <Switch />
          </Form.Item>
        </div>
        <span className={styles.hint}>
          The minimum compares holdings priced in the chosen currency only;
          others never qualify. Leave it empty to turn it off: 0 still excludes
          other currencies and unpriced instruments.
        </span>
      </section>

      <div style={{display: 'flex', gap: 12}}>
        <DBButton
          label={previewing ? 'Previewing…' : 'Preview audience'}
          onClick={runPreview}
          disabled={!selections || previewing}
        />
        {canExport && (
          <DBButton
            type="secondary"
            label={exporting ? 'Exporting…' : 'Export CSV'}
            onClick={runExport}
            disabled={
              !selections || exporting || preview?.estimatedRecipients === 0
            }
          />
        )}
      </div>
      {preview && <PreviewResult preview={preview} />}
    </div>
  );
};
